import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Trash2 } from "lucide-react";

const CONTACT_EMAIL = "marek.wroblewski28@gmail.com";
const EMAIL_SUBJECT = "SubZero account deletion request";

const CONTENT = {
  en: {
    title: "Delete Account and Data – SubZero: AI Receipt Scanner",
    updated: "Last updated: 16 June 2026",
    introH: "How to delete your account and data",
    intro:
      "SubZero: AI Receipt Scanner is provided by Pewnosc Siebie AI. You can request deletion of your account and the personal data associated with it at any time, free of charge, by sending us an email.",
    howH: "How to request deletion",
    steps: [
      "Send an email to " + CONTACT_EMAIL + ".",
      'Use the exact subject line: "' + EMAIL_SUBJECT + '".',
      "Send the request from the same Google account / email address you used to sign in to the app, so we can verify ownership.",
    ],
    emailBtn: "Email a deletion request",
    dataH: "What data will be deleted",
    dataIntro: "Where applicable, the following data linked to your account will be deleted:",
    dataItems: [
      "Google sign-in account reference / app user profile",
      "Receipt images",
      "OCR / extracted receipt data",
      "Expense records",
      "Subscription records",
      "Dashboard data",
      "User settings connected with the account",
    ],
    retainH: "Limited retention",
    retain:
      "Some limited records may be retained only where required for legal, security, fraud prevention, accounting, payment or compliance reasons. Such records are kept only for as long as necessary and then deleted.",
    timeH: "Processing time",
    time: "We will process and complete your deletion request within 30 days after we confirm the request.",
    relatedH: "Related documents",
    back: "Back",
  },
  pl: {
    title: "Usu\u0144 konto i dane \u2013 SubZero: AI Receipt Scanner",
    updated: "Ostatnia aktualizacja: 16 czerwca 2026",
    introH: "Jak usun\u0105\u0107 konto i dane",
    intro:
      "Aplikacja SubZero: AI Receipt Scanner jest dostarczana przez Pewnosc Siebie AI. W ka\u017cdej chwili mo\u017cesz bezp\u0142atnie poprosi\u0107 o usuni\u0119cie swojego konta i powi\u0105zanych z nim danych osobowych, wysy\u0142aj\u0105c do nas wiadomo\u015b\u0107 e-mail.",
    howH: "Jak z\u0142o\u017cy\u0107 pro\u015bb\u0119 o usuni\u0119cie",
    steps: [
      "Wy\u015blij wiadomo\u015b\u0107 e-mail na adres " + CONTACT_EMAIL + ".",
      'U\u017cyj dok\u0142adnego tematu: "' + EMAIL_SUBJECT + '".',
      "Wy\u015blij pro\u015bb\u0119 z tego samego konta Google / adresu e-mail, kt\u00f3rego u\u017cywasz do logowania w aplikacji, aby\u015bmy mogli zweryfikowa\u0107 w\u0142a\u015bciciela.",
    ],
    emailBtn: "Wy\u015blij pro\u015bb\u0119 o usuni\u0119cie",
    dataH: "Jakie dane zostan\u0105 usuni\u0119te",
    dataIntro: "Tam, gdzie ma to zastosowanie, usuni\u0119te zostan\u0105 nast\u0119puj\u0105ce dane powi\u0105zane z Twoim kontem:",
    dataItems: [
      "Referencja konta logowania Google / profil u\u017cytkownika aplikacji",
      "Obrazy paragon\u00f3w",
      "Dane OCR / wyodr\u0119bnione dane z paragon\u00f3w",
      "Zapisy wydatk\u00f3w",
      "Zapisy subskrypcji",
      "Dane pulpitu",
      "Ustawienia u\u017cytkownika powi\u0105zane z kontem",
    ],
    retainH: "Ograniczone przechowywanie",
    retain:
      "Niekt\u00f3re ograniczone dane mog\u0105 by\u0107 zachowane wy\u0142\u0105cznie wtedy, gdy jest to wymagane ze wzgl\u0119d\u00f3w prawnych, bezpiecze\u0144stwa, zapobiegania oszustwom, ksi\u0119gowych, p\u0142atniczych lub zgodno\u015bci. Takie dane s\u0105 przechowywane tylko przez niezb\u0119dny czas, a nast\u0119pnie usuwane.",
    timeH: "Czas realizacji",
    time: "Pro\u015bb\u0119 o usuni\u0119cie zrealizujemy w ci\u0105gu 30 dni od potwierdzenia pro\u015bby.",
    relatedH: "Powi\u0105zane dokumenty",
    back: "Wstecz",
  },
};

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  // Legal body: PL/EN only; VI shows the EN body (page chrome is localized via t()).
  const c = CONTENT[lang] || CONTENT.en;
  const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`;

  const related = [
    { to: "/privacy", label: t("settings.privacy") },
    { to: "/terms", label: t("settings.terms") },
    { to: "/ai-notice", label: t("settings.aiNotice") },
  ];

  return (
    <div className="min-h-screen bg-surface-0" data-testid="delete-account-page">
      <header className="border-b border-white/10 sticky top-0 bg-surface-0/90 backdrop-blur z-30">
        <div className="mx-auto max-w-[820px] px-4 md:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid="delete-account-back"
          >
            <ArrowLeft style={{ width: 16, height: 16 }} /> {t("common.back")}
          </button>
          <Logo size="sm" />
        </div>
      </header>

      <div className="mx-auto max-w-[820px] px-4 md:px-8 py-10 pb-24">
        <div className="flex justify-end mb-6">
          <LangToggle data-testid="delete-account-language-toggle" />
        </div>

        <article className="card-base p-6 md:p-8">
          <div className="flex items-start gap-3">
            <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-500/10 border border-rose-400/25">
              <Trash2 className="text-rose-300" style={{ width: 18, height: 18 }} />
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight" data-testid="delete-account-title">
                {c.title}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">{c.updated}</p>
            </div>
          </div>

          <div className="space-y-7 max-w-prose mt-7">
            <section>
              <h2 className="font-display font-semibold text-lg mb-1.5">{c.introH}</h2>
              <p className="text-sm md:text-base text-muted-foreground leading-7">{c.intro}</p>
            </section>

            <section>
              <h2 className="font-display font-semibold text-lg mb-2">{c.howH}</h2>
              <ol className="list-decimal pl-5 space-y-1.5 text-sm md:text-base text-muted-foreground leading-7">
                {c.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
              <a href={mailto} data-testid="delete-account-email-link">
                <Button className="mt-4 h-11 bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
                  <Mail style={{ width: 16, height: 16 }} className="mr-2" /> {c.emailBtn}
                </Button>
              </a>
            </section>

            <section>
              <h2 className="font-display font-semibold text-lg mb-2">{c.dataH}</h2>
              <p className="text-sm md:text-base text-muted-foreground leading-7 mb-2">{c.dataIntro}</p>
              <ul className="list-disc pl-5 space-y-1 text-sm md:text-base text-muted-foreground leading-7">
                {c.dataItems.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-display font-semibold text-lg mb-1.5">{c.retainH}</h2>
              <p className="text-sm md:text-base text-muted-foreground leading-7">{c.retain}</p>
            </section>

            <section>
              <h2 className="font-display font-semibold text-lg mb-1.5">{c.timeH}</h2>
              <p className="text-sm md:text-base text-muted-foreground leading-7">{c.time}</p>
            </section>

            <section className="border-t border-white/10 pt-6">
              <h2 className="font-display font-semibold text-lg mb-3">{c.relatedH}</h2>
              <div className="flex flex-wrap gap-2">
                {related.map((r) => (
                  <Button
                    key={r.to}
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(r.to)}
                    className="border-white/15 bg-white/5 hover:bg-white/10"
                    data-testid={`delete-account-link-${r.to.replace("/", "")}`}
                  >
                    {r.label}
                  </Button>
                ))}
              </div>
            </section>
          </div>
        </article>
      </div>
    </div>
  );
}
